import React, { useEffect, useRef, useState } from "react";
import { History, RotateCcw, ArrowRightLeft, Bot, Loader2, Mail, MessageSquare, Mic, Pencil, Send, Square, Trash2, X, Check } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

const FILTERS = [["all", "Tout"], ["comments", "Commentaires"], ["emails", "Emails"], ["statuts", "Statuts"]];
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" }) : "");
const ICONS = { comment: MessageSquare, ia: Bot, email: Mail, status: ArrowRightLeft, send: Send, log: History };

export function useRecorder(onText) {
  const [state, setState] = useState("idle");
  const rec = useRef(null);
  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const chunks = [];
      const mr = new MediaRecorder(stream);
      mr.ondataavailable = (e) => e.data?.size && chunks.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setState("busy");
        try {
          const fd = new FormData();
          fd.append("audio", new Blob(chunks, { type: mr.mimeType || "audio/webm" }), (mr.mimeType || "").includes("mp4") ? "dictee.m4a" : "dictee.webm");
          const { data } = await api.post("/assistant/transcribe", fd);
          if (data?.text?.trim()) onText(data.text.trim()); else toast.warning("Rien d'audible n'a été détecté");
        } catch (e) { toast.error(e?.response?.data?.detail || "Transcription impossible"); }
        finally { setState("idle"); }
      };
      rec.current = mr;
      mr.start();
      setState("rec");
    } catch { toast.error("Microphone inaccessible — autorisez le micro dans le navigateur"); }
  };
  return { state, toggle: () => (state === "rec" ? rec.current?.stop() : start()) };
}

function Composer({ dossierId, tab, savId, onAdded }) {
  const [text, setText] = useState("");
  const [source, setSource] = useState("texte");
  const [busy, setBusy] = useState(false);
  const mic = useRecorder((t) => { setText((x) => (x ? `${x} ${t}` : t)); setSource("dictée"); });
  const post = async () => {
    setBusy(true);
    try { await api.post(`/dossiers/${dossierId}/tabs/${tab}/comments`, { text, source, ...(savId ? { sav_id: savId } : {}) }); setText(""); setSource("texte"); onAdded(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Enregistrement impossible"); }
    finally { setBusy(false); }
  };
  return (
    <div className="flex items-end gap-2">
      <textarea value={text} onChange={(e) => setText(e.target.value)} rows={2} placeholder="Écrire un commentaire pour l'équipe…" className="input-field text-sm flex-1" data-testid={`feed-input-${tab}`}
        onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey) && text.trim()) post(); }} />
      <button type="button" onClick={mic.toggle} disabled={mic.state === "busy"} title="Dicter" className={`p-2.5 rounded-md border transition-colors ${mic.state === "rec" ? "bg-red-50 border-red-300 text-red-700" : "border-norea-border text-norea-textMuted hover:text-norea-text"}`} data-testid={`feed-mic-${tab}`}>
        {mic.state === "busy" ? <Loader2 className="w-4 h-4 animate-spin" /> : mic.state === "rec" ? <Square className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
      </button>
      <button onClick={post} disabled={busy || !text.trim()} className="btn-primary text-sm" data-testid={`feed-submit-${tab}`}>{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Publier</button>
    </div>
  );
}

function FeedItem({ it, onChanged }) {
  const [edit, setEdit] = useState(null);
  const Icon = ICONS[it.type] || MessageSquare;
  const save = async () => {
    try { await api.put(`/dossiers/comments/${it.id}`, { text: edit }); setEdit(null); onChanged(); } catch (e) { toast.error(e?.response?.data?.detail || "Modification impossible"); }
  };
  const del = async () => {
    if (!window.confirm("Supprimer ce commentaire ?")) return;
    try { await api.delete(`/dossiers/comments/${it.id}`); onChanged(); } catch (e) { toast.error(e?.response?.data?.detail || "Suppression impossible"); }
  };
  return (
    <li className="flex gap-3 py-2.5 border-b border-norea-border last:border-0 group" data-testid={`feed-item-${it.id}`}>
      <span className={`mt-0.5 w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${it.type === "ia" ? "bg-violet-100 text-violet-700" : "bg-norea-surfaceHover text-norea-textMuted"}`}><Icon className="w-3.5 h-3.5" /></span>
      <div className="flex-1 min-w-0">
        <div className="text-xs text-norea-textMuted flex items-center gap-2 flex-wrap">
          <strong className="text-norea-text" data-testid={`feed-author-${it.id}`}>{it.author_name}</strong>
          {it.type === "ia" && <span className="text-[9px] uppercase font-semibold tracking-wider px-1 rounded bg-violet-100 text-violet-700">IA</span>}
          {it.source === "dictée" && <span className="text-[10px] italic">dicté</span>}
          <span data-testid={`feed-date-${it.id}`}>{fmt(it.at)}</span>
          {it.updated_at && <span className="italic">(modifié)</span>}
          {it.type === "ia" && it.email_id && <button onClick={async () => {
            if (!window.confirm("Annuler tout ce que l'IA a fait pour cet email (résumé, tickets, propositions, pièces rangées, champs remplis) ?")) return;
            try { const { data } = await api.post(`/dossiers/comments/${it.id}/undo`); toast.success(`Actions IA annulées${data.tickets ? ` — ${data.tickets} ticket(s) supprimé(s)` : ""}`); onChanged(); }
            catch (e) { toast.error(e?.response?.data?.detail || "Annulation impossible"); }
          }} className="ml-auto text-[11px] text-norea-textMuted hover:text-red-700 inline-flex items-center gap-1" data-testid={`feed-undo-${it.id}`}><RotateCcw className="w-3 h-3" /> Annuler</button>}
          {it.can_edit && !edit && <span className="ml-auto opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
            <button onClick={() => setEdit(it.text)} className="p-1 hover:text-norea-text" data-testid={`feed-edit-${it.id}`}><Pencil className="w-3 h-3" /></button>
            <button onClick={del} className="p-1 hover:text-red-700" data-testid={`feed-delete-${it.id}`}><Trash2 className="w-3 h-3" /></button>
          </span>}
        </div>
        {edit !== null ? (
          <div className="mt-1 flex gap-2"><textarea value={edit} onChange={(e) => setEdit(e.target.value)} rows={2} className="input-field text-sm flex-1" data-testid={`feed-edit-input-${it.id}`} />
            <button onClick={save} className="p-2 text-emerald-700" data-testid={`feed-edit-save-${it.id}`}><Check className="w-4 h-4" /></button><button onClick={() => setEdit(null)} className="p-2"><X className="w-4 h-4" /></button></div>
        ) : (
          <div className="text-sm text-norea-text whitespace-pre-wrap mt-0.5">
            {it.type === "log" ? <>{it.text}{(it.before || it.after) && <span className="text-norea-textMuted"> : {String(it.before ?? "—")} → <strong className="text-norea-text">{String(it.after ?? "—")}</strong></span>}</>
              : it.type === "status" ? <>Statut : {it.before || "À faire"} → <strong>{it.after}</strong></> : it.type === "send" ? <>Envoi : {it.text}{it.dest && <span className="text-norea-textMuted"> — {it.dest}</span>}</>
              : it.type === "email" ? <>{it.direction === "out" ? "Email envoyé" : "Email reçu"} : <strong>{it.text}</strong><div className="text-xs text-norea-textMuted line-clamp-2">{it.excerpt}</div></> : it.text}
            {it.email_subject && <div className="text-xs text-norea-textMuted mt-0.5">Email d'origine : {it.email_subject}</div>}
          </div>
        )}
      </div>
    </li>
  );
}

export default function TabFeed({ dossierId, tab, refreshKey, savId, title = "Commentaires et échanges" }) {
  const [filter, setFilter] = useState("all");
  const [items, setItems] = useState(null);
  const load = () => api.get(`/dossiers/${dossierId}/tabs/${tab}/feed`, { params: { filter, ...(savId ? { sav_id: savId } : {}) } }).then((r) => setItems(r.data.items)).catch(() => setItems([]));
  useEffect(() => { load(); }, [dossierId, tab, filter, refreshKey, savId]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <section className="mt-6 card-flat p-5" data-testid={`tab-feed-${tab}`}>
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <h3 className="font-heading text-base font-medium text-norea-text">{title}</h3>
        <div className="inline-flex p-0.5 rounded-md border border-norea-border bg-white">
          {FILTERS.map(([k, l]) => <button key={k} onClick={() => setFilter(k)} className={`px-2.5 py-1 text-xs rounded transition-colors ${filter === k ? "bg-norea-primary text-white" : "text-norea-textMuted hover:text-norea-text"}`} data-testid={`feed-filter-${k}-${tab}`}>{l}</button>)}
        </div>
      </div>
      <Composer dossierId={dossierId} tab={tab} savId={savId} onAdded={load} />
      {items === null ? <div className="py-6 flex justify-center"><Loader2 className="w-4 h-4 animate-spin" /></div>
        : items.length === 0 ? <div className="py-6 text-center text-xs italic text-norea-textMuted" data-testid={`feed-empty-${tab}`}>Aucun échange pour l'instant.</div>
        : <ul className="mt-3" data-testid={`feed-list-${tab}`}>{items.map((it) => <FeedItem key={`${it.type}-${it.id}`} it={it} onChanged={load} />)}</ul>}
    </section>
  );
}
